import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { listLocations } from '@/features/inventory/api'
import {
  TERMINAL_STATUSES,
  TERMINAL_STATUS_LABELS,
  createTerminal,
  updateTerminal,
  type Terminal,
} from '@/features/pos/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const schema = z.object({
  code: z.string().trim().min(1, 'Enter a code').max(30),
  name: z.string().trim().min(1, 'Enter a name').max(100),
  location_id: z.string().trim().min(1, 'Pick a store'),
  status: z.enum(TERMINAL_STATUSES),
  notes: z.string().trim().max(500),
})

type FormValues = z.infer<typeof schema>

/**
 * Create and edit in one dialog, following `LocationFormDialog`'s shape. `code` and
 * `location_id` are immutable once set -- `TerminalUpdate` has no fields for either, since
 * moving a till to a different store is a new till, not an edit -- so both are editable
 * only while creating.
 */
export function TerminalFormDialog({
  open,
  terminal,
  onClose,
}: {
  open: boolean
  terminal?: Terminal
  onClose: () => void
}) {
  const isEdit = terminal !== undefined
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  // Only stores get a till (see `pos.router` module docstring) -- a warehouse or the online
  // channel would have nowhere for the cash to go.
  const locations = useQuery({
    queryKey: ['stock-locations'],
    queryFn: listLocations,
    retry: false,
    enabled: open,
  })
  const storeOptions = (locations.data?.data ?? []).filter((location) => location.type === 'store')

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: terminal?.code ?? '',
      name: terminal?.name ?? '',
      location_id: terminal?.location_id ?? '',
      status: terminal?.status ?? 'active',
      notes: terminal?.notes ?? '',
    },
  })

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      terminal
        ? updateTerminal(terminal.id, {
            name: values.name,
            status: values.status,
            notes: values.notes || null,
          })
        : createTerminal({
            code: values.code,
            name: values.name,
            location_id: values.location_id,
            status: values.status,
            notes: values.notes || null,
          }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pos-terminals'] })
      toast.success(isEdit ? 'Till saved.' : 'Till added.')
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError ? error.message : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? `Edit ${terminal.name}` : 'New till'}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="terminal-form" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Add till'}
            </Button>
          </>
        }
      >
        <form id="terminal-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Code"
              error={errors.code?.message}
              hint={isEdit ? 'Fixed once created.' : 'e.g. TILL-ANDHERI-2'}
            >
              {(props) => (
                <Input {...props} {...register('code')} autoComplete="off" disabled={isEdit} />
              )}
            </Field>
            <Field label="Name" error={errors.name?.message}>
              {(props) => <Input {...props} {...register('name')} autoComplete="off" />}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Store"
              error={errors.location_id?.message}
              hint={isEdit ? 'Fixed once created.' : undefined}
            >
              {(props) => (
                <select
                  {...props}
                  {...register('location_id')}
                  className={SELECT_CLASS}
                  disabled={isEdit || locations.isLoading}
                >
                  <option value="">Pick a store…</option>
                  {storeOptions.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Status" error={errors.status?.message}>
              {(props) => (
                <select {...props} {...register('status')} className={SELECT_CLASS}>
                  {TERMINAL_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {TERMINAL_STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>

          <Field label="Notes" error={errors.notes?.message} hint="Optional.">
            {(props) => <Input {...props} {...register('notes')} autoComplete="off" />}
          </Field>
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
